import { LoadingOutlined } from "@ant-design/icons";
import { FormDrawer, buildInitialValues, buildValidationSchema } from "Components";
import { useTranslation } from "contexts/TranslationContext";
import { withFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
	useGetEducationLevelsQuery,
	useGetEmploymentCategoriesQuery,
	useGetEmploymentSectorsQuery,
	useGetOccupationsQuery,
	useGetRelatedPartyQuery,
	useGetResidentialStatusQuery
} from "services/lookupSelector";
import { PATCH_EMPLOYMENT_DETAILS, SET_ACTIVE_STEP_INDEX, SET_STEP_VALIDITY, useCreateRequestContext } from "./CreateRequestContext";
import { employmentDetailsSchema } from "./schemas";

const EmploymentDetails = withFormik({
	mapPropsToValues: () => ({
		...buildInitialValues(
			employmentDetailsSchema({
				hideRealBenificiaryName: true,
				employmentTypes: [],
				residentStatuses: [],
				educationLevels: [],
				employmentSectors: [],
				relatedParty: [],
				occupations: []
			})
		),
	}),
	validationSchema: buildValidationSchema(
		employmentDetailsSchema({
			hideRealBenificiaryName: true,
			employmentTypes: [],
			residentStatuses: [],
			educationLevels: [],
			employmentSectors: [],
			relatedParty: [],
			occupations: []
		})
	),
	enableReinitialize: true,
	validateOnMount: true,
	// validateOnChange: true,
	// validateOnBlur: true,

})((props) => {
	const { state, dispatch } = useCreateRequestContext();
	const { translate } = useTranslation();

	// RTK Query hooks for lookup data
	const { data: employmentSectorsData, isLoading: sectorsLoading } = useGetEmploymentSectorsQuery({ status: 'ACTIVE' });
	const { data: educationLevelsData, isLoading: educationLoading } = useGetEducationLevelsQuery({ status: 'ACTIVE' });
	const { data: residentialStatusData, isLoading: residentialLoading } = useGetResidentialStatusQuery({ status: 'ACTIVE' });
	const { data: employmentCategoriesData, isLoading: categoriesLoading } = useGetEmploymentCategoriesQuery({ status: 'ACTIVE' });
	const { data: relatedPartyData, isLoading: relatedPartyLoading } = useGetRelatedPartyQuery({ status: 'ACTIVE' });
	const { data: occupationsData, isLoading: occupationsLoading } = useGetOccupationsQuery({ status: 'ACTIVE' });

	const [employmentTypes, setEmploymentTypes] = useState([]);
	const [residentStatuses, setResidentStatuses] = useState([]);
	const [educationLevels, setEducationLevels] = useState([]);
	const [employmentSectors, setEmploymentSectors] = useState([]);
	const [relatedParty, setRelatedParty] = useState([]);
	const [occupations, setOccupations] = useState([]);

	// Check if lookup data is still loading
	const initialDataLoading = sectorsLoading || educationLoading || residentialLoading ||
		categoriesLoading || relatedPartyLoading || occupationsLoading;


	const initializeData = () => {
		try {
			// Process employment sectors data
			if (employmentSectorsData?.payload?.rows) {
				setEmploymentSectors(
					employmentSectorsData.payload.rows.map((row) => ({
						label: row.DESCRIPTION, // Standardized field name
						value: String(row.ID), // Standardized field name
						labelAr: String(row.DESCRIPTIONAR), // Standardized field name
					}))
				);
			}

			// Process education levels data
			if (educationLevelsData?.payload?.rows) {
				setEducationLevels(
					educationLevelsData.payload.rows.map((row) => ({
						label: row.DESCRIPTION,
						value: String(row.ID),
						labelAr: String(row.DESCRIPTIONAR),
					}))
				);
			}

			// Process residential status data
			if (residentialStatusData?.payload?.rows) {
				setResidentStatuses(
					residentialStatusData.payload.rows.map((row) => ({
						label: row.DESCRIPTION,
						value: String(row.ID),
						labelAr: String(row.DESCRIPTIONAR),
					}))
				);
			}

			// Process employment categories data
			if (employmentCategoriesData?.payload?.rows) {
				setEmploymentTypes(
					employmentCategoriesData.payload.rows.map((row) => ({
						label: row.DESCRIPTION,
						value: String(row.ID),
						labelAr: String(row.DESCRIPTIONAR),
					}))
				);
			}

			// Process related party data
			if (relatedPartyData?.payload?.rows) {
				setRelatedParty(
					relatedPartyData.payload.rows.map((row) => ({
						label: row.DESCRIPTION,
						value: String(row.ID),
						labelAr: String(row.DESCRIPTIONAR),
					}))
				);
			}

			// Process occupations data
			if (occupationsData?.payload?.rows) {
				setOccupations(
					occupationsData.payload.rows.map((row) => ({
						label: row.DESCRIPTION,
						value: String(row.ID),
						labelAr: String(row.DESCRIPTIONAR),
					}))
				);
			}

			// Set initial form values after data is processed
			if (!initialDataLoading) {
				props.setValues({
					...state.employmentDetails,
				});
			}
		} catch (error) {
			console.error('Error processing lookup data:', error);
			toast.error("Failed to load lookup data.");
		}
	};

	// Effect to process lookup data when it becomes available
	useEffect(() => {
		if (!initialDataLoading) {
			initializeData();
		}
	}, [
		employmentSectorsData,
		educationLevelsData,
		residentialStatusData,
		employmentCategoriesData,
		relatedPartyData,
		occupationsData,
		initialDataLoading
	]);


	useEffect(() => {
		dispatch({ type: SET_ACTIVE_STEP_INDEX, payload: 1 });
	}, [dispatch]);

	useEffect(() => {
		dispatch({
			type: SET_STEP_VALIDITY,
			payload: {
				stepIndex: 1,
				valid: props.isValid,
			},
		});
	}, [dispatch, props.isValid]);

	useEffect(() => {
		dispatch({
			type: PATCH_EMPLOYMENT_DETAILS,
			payload: props.values,
		});
	}, [dispatch, props.values]);

	useEffect(() => { //setting beneficiary name and relatedPartyId to null based on true false
		if (props.values?.beneficiary === 1) {
			props.setValues((prev) => ({
				...prev,
				beneficiaryName: "",
			}));
		}

		if (props.values?.relatedPartyBoolean === 0) {
			props.setValues((prev) => ({
				...prev,
				partyId: null,
			}));
		}
	}, [props.values?.beneficiary, props.values?.relatedPartyBoolean]);

	const schemaFields = useMemo(() => {
		let hideRealBenificiaryName = true
		let hideRelatedParty = true
		if (props.values?.beneficiary === 0) { //enable realBenificiaryName if option is no
			hideRealBenificiaryName = false
		}
		if (props.values?.relatedPartyBoolean === 1) { //enable relatedParty if option is yes
			hideRelatedParty = false
		}
		return employmentDetailsSchema({
			hideRealBenificiaryName,
			hideRelatedParty,
			employmentSectors,
			employmentTypes,
			residentStatuses,
			educationLevels,
			relatedParty,
			occupations
		});
	}, [employmentSectors, employmentTypes, residentStatuses, educationLevels, relatedParty, props.values?.beneficiary, props.values?.relatedPartyBoolean, occupations]);

	if (initialDataLoading) {
		return (
			<div className='p-5 bg-white flex flex-col gap-4'>
				<div className='flex gap-4'>
					<LoadingOutlined />
					<span>{translate("CREATE_REQUEST.LOADING_EMPLOYMENT_DETAILS")}</span>
				</div>
			</div>
		);
	}

	return (
		<>
			<h2 className='font-bold text-xl mb-4'>{translate("CREATE_REQUEST.EMPLOYMENT_DETAILS")}</h2>

			<div className='mb-4'>
				<FormDrawer
					schema={{
						fields: schemaFields,
					}}
					values={props.values}
					onChange={props.handleChange}
				/>
			</div>
		</>
	);
});

export default EmploymentDetails;